import { AppBar, Box, Button, IconButton, Stack, Toolbar, Tooltip, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MailIcon from '@mui/icons-material/Mail';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useRouterState, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import { LangToggle } from './LangToggle';
import { isAuthenticated, logout, logoutAll, fetchMe } from '@/features/auth';

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Subscribe to router state so the bar re-renders on navigation (login/logout).
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const authed = isAuthenticated();

  const meQ = useQuery({ queryKey: ['me'], queryFn: fetchMe, enabled: authed, retry: false });
  const me = meQ.data;

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const handleLogoutAll = async () => {
    if (!window.confirm(t('mail.common.logoutAllConfirm'))) return;
    await logoutAll();
    window.location.href = '/';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Logo size="small" />
          <Stack direction="row" spacing={1} sx={{ marginLeft: 'auto' }} alignItems="center">
            {authed && me && (
              <>
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<MailIcon />}
                  onClick={() => navigate({ to: '/mailbox' })}
                  variant={pathname === '/mailbox' ? 'outlined' : 'text'}
                >
                  {t('mail.nav.mail')}
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<FilterAltIcon />}
                  onClick={() => navigate({ to: '/filters' })}
                  variant={pathname === '/filters' ? 'outlined' : 'text'}
                >
                  {t('mail.nav.filters')}
                </Button>
                {me.is_admin && (
                  <>
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<AdminPanelSettingsIcon />}
                      onClick={() => navigate({ to: '/admin' })}
                      variant={pathname === '/admin' ? 'outlined' : 'text'}
                    >
                      {t('mail.nav.admin')}
                    </Button>
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => navigate({ to: '/logs' })}
                      variant={pathname === '/logs' ? 'outlined' : 'text'}
                    >
                      {t('mail.nav.logs')}
                    </Button>
                  </>
                )}
              </>
            )}
            {authed && me && (
              <Typography variant="body2" sx={{ opacity: 0.85, display: { xs: 'none', sm: 'block' } }}>
                {me.display_name}
              </Typography>
            )}
            <LangToggle />
            <ThemeToggle />
            {authed && (
              <Tooltip title={t('mail.common.logoutAll')}>
                <IconButton color="inherit" onClick={handleLogoutAll} aria-label={t('mail.common.logoutAll')}>
                  <DevicesOtherIcon />
                </IconButton>
              </Tooltip>
            )}
            {authed && (
              <Tooltip title={t('mail.common.logout')}>
                <IconButton color="inherit" onClick={handleLogout} aria-label={t('mail.common.logout')}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Toolbar>
      </AppBar>
      <Box sx={{ flex: 1, overflow: 'auto' }}>{children}</Box>
    </Box>
  );
}
